import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Image,
  Link,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  VStack,
} from '@chakra-ui/react';
import useDesignScore from 'hooks/useDesignScore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon from 'components/Icon';
import Text from 'components/Text';

import CodeFrame from '../CodeFrame';
import DevSandboxTooltip from '../DevSandboxTooltip';

interface IOverlayScreenshot {
  url: string;
  title: string;
}

export interface IScoreConfig {
  designScreenshotUrl: string;
  screenshotWidth: number;
  screenshotHeight: number;
}

interface IBrowserTabProps {
  browserUrl: string;
  codeFrameUrl: string;
  screenshotUrl?: string | null;
  isExternalSolution?: boolean | null;
  repositoryUrl?: string;
  previewUrl?: string;
  onCodeFrameUrlChange: () => void;
  designScreenshotUrl?: string | null;
  showOverlayControls?: boolean;
  overlayScreenshots?: IOverlayScreenshot[];
  scoreConfig?: IScoreConfig | null;
}

const BrowserTab = ({
  browserUrl,
  codeFrameUrl,
  isExternalSolution,
  screenshotUrl,
  repositoryUrl,
  previewUrl,
  designScreenshotUrl,
  showOverlayControls = false,
  overlayScreenshots,
  scoreConfig,
}: IBrowserTabProps) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(100);
  const [currentOverlayIndex, setCurrentOverlayIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // For projects with multiple card screenshots, compare against the active card
  const activeScoreConfig = useMemo(() => {
    if (!scoreConfig) return null;
    if (overlayScreenshots && overlayScreenshots.length > 0) {
      return {
        ...scoreConfig,
        designScreenshotUrl: overlayScreenshots[currentOverlayIndex]?.url || scoreConfig.designScreenshotUrl,
      };
    }
    return scoreConfig;
  }, [scoreConfig, overlayScreenshots, currentOverlayIndex]);

  const { score: designScore, isComparing } = useDesignScore(codeFrameUrl, activeScoreConfig, containerRef);

  const hasMultipleScreenshots = overlayScreenshots && overlayScreenshots.length > 1;
  const activeScreenshotUrl =
    overlayScreenshots && overlayScreenshots.length > 0
      ? overlayScreenshots[currentOverlayIndex]?.url
      : designScreenshotUrl;
  const activeScreenshotTitle =
    overlayScreenshots && overlayScreenshots.length > 0
      ? overlayScreenshots[currentOverlayIndex]?.title
      : null;
  const toggleOverlay = useCallback(() => {
    setIsOverlayVisible((prev) => !prev);
  }, []);

  const goToPrevScreenshot = useCallback(() => {
    if (!overlayScreenshots || overlayScreenshots.length <= 1) return;
    setCurrentOverlayIndex((prev) => (prev === 0 ? overlayScreenshots.length - 1 : prev - 1));
  }, [overlayScreenshots]);

  const goToNextScreenshot = useCallback(() => {
    if (!overlayScreenshots || overlayScreenshots.length <= 1) return;
    setCurrentOverlayIndex((prev) => (prev === overlayScreenshots.length - 1 ? 0 : prev + 1));
  }, [overlayScreenshots]);

  // Keyboard shortcut: Cmd/Ctrl+Shift+D to toggle overlay
  // (Cmd+D alone bookmarks the page in most browsers)
  useEffect(() => {
    if (!showOverlayControls || !designScreenshotUrl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        e.stopPropagation();
        toggleOverlay();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showOverlayControls, designScreenshotUrl, toggleOverlay]);

  // Keyboard shortcut: Left/Right arrow keys to navigate overlay screenshots
  useEffect(() => {
    if (!isOverlayVisible || !hasMultipleScreenshots) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        goToPrevScreenshot();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        goToNextScreenshot();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOverlayVisible, hasMultipleScreenshots, goToPrevScreenshot, goToNextScreenshot]);

  if (!browserUrl) {
    return null;
  }

  const canShowOverlay = showOverlayControls && (activeScreenshotUrl || designScreenshotUrl);

  return (
    <Box ref={containerRef} width="100%" height="100%" position="relative">
      {/* Preview toolbar - overlay controls */}
      {canShowOverlay && (
        <HStack
          position="absolute"
          top={2}
          right={2}
          zIndex={10}
          spacing={0}
          align="flex-start"
        >
          {/* Expanded controls — renders to the LEFT of the toggle button */}
          {isOverlayVisible && (
            <Box
              bg="blackAlpha.600"
              backdropFilter="blur(8px)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.100"
              boxShadow="0 2px 8px rgba(0,0,0,0.3)"
              mr={1.5}
            >
              <HStack px={2} py={1.5} spacing={2.5}>
                <Text fontSize="micro" color="whiteAlpha.500" whiteSpace="nowrap" userSelect="none">
                  {overlayOpacity}%
                </Text>
                <Slider
                  aria-label="Overlay opacity"
                  value={overlayOpacity}
                  onChange={setOverlayOpacity}
                  min={10}
                  max={100}
                  step={5}
                  width="80px"
                >
                  <SliderTrack bg="whiteAlpha.200" h="4px" borderRadius="full">
                    <SliderFilledTrack bg="purple.400" borderRadius="full" />
                  </SliderTrack>
                  <SliderThumb boxSize={3} bg="white" boxShadow="0 1px 3px rgba(0,0,0,0.4)" />
                </Slider>
              </HStack>

              {/* Screenshot navigation — second row */}
              {hasMultipleScreenshots && overlayScreenshots && (
                <HStack
                  px={1.5}
                  py={1}
                  justifyContent="space-between"
                  borderTop="1px solid"
                  borderColor="whiteAlpha.100"
                >
                  <IconButton
                    aria-label="Previous card screenshot (←)"
                    icon={<ChevronLeftIcon boxSize={4} />}
                    size="xs"
                    variant="ghost"
                    color="whiteAlpha.700"
                    _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
                    _active={{ bg: 'whiteAlpha.300' }}
                    onClick={goToPrevScreenshot}
                    borderRadius="md"
                  />
                  <Text fontSize="micro" color="whiteAlpha.600" whiteSpace="nowrap" cursor="default">
                    {currentOverlayIndex + 1} / {overlayScreenshots.length}
                  </Text>
                  <IconButton
                    aria-label="Next card screenshot (→)"
                    icon={<ChevronRightIcon boxSize={4} />}
                    size="xs"
                    variant="ghost"
                    color="whiteAlpha.700"
                    _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
                    _active={{ bg: 'whiteAlpha.300' }}
                    onClick={goToNextScreenshot}
                    borderRadius="md"
                  />
                </HStack>
              )}
            </Box>
          )}

          {/* Toggle button — always in the same position (rightmost) */}
          <DevSandboxTooltip
            label={isOverlayVisible ? 'Hide design overlay (⌘⇧D)' : 'Show design overlay (⌘⇧D)'}
          >
            <IconButton
              aria-label={isOverlayVisible ? 'Hide design overlay' : 'Show design overlay'}
              icon={isOverlayVisible ? <ViewOffIcon boxSize={4} /> : <ViewIcon boxSize={4} />}
              size="sm"
              minW="36px"
              h="36px"
              variant="ghost"
              color={isOverlayVisible ? 'purple.300' : 'whiteAlpha.600'}
              bg={isOverlayVisible ? 'blackAlpha.600' : 'blackAlpha.500'}
              backdropFilter="blur(8px)"
              borderRadius="lg"
              border="1px solid"
              borderColor={isOverlayVisible ? 'purple.500' : 'whiteAlpha.100'}
              boxShadow={isOverlayVisible ? '0 0 8px rgba(159, 122, 234, 0.25), 0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.3)'}
              _hover={{
                color: isOverlayVisible ? 'purple.200' : 'whiteAlpha.900',
                bg: isOverlayVisible ? 'blackAlpha.700' : 'blackAlpha.600',
                borderColor: isOverlayVisible ? 'purple.400' : 'whiteAlpha.200',
              }}
              _active={{ bg: 'blackAlpha.700' }}
              onClick={toggleOverlay}
              transition="all 0.15s ease"
            />
          </DevSandboxTooltip>
        </HStack>
      )}

      {/* Main content */}
      {isExternalSolution ? (
        <Center width="100%" height="100%">
          <VStack spacing={4}>
            {repositoryUrl && (
              <Link
                href={repositoryUrl}
                isExternal
                _hover={{ textDecoration: 'none', opacity: 0.8 }}
              >
                <HStack
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="xl"
                  px={5}
                  py={3}
                  spacing={3}
                  _hover={{ bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.300' }}
                  transition="all 0.15s ease"
                >
                  <Icon type="Github" size={20} />
                  <Text fontSize="small" fontWeight="semibold">
                    View Code
                  </Text>
                  <ExternalLinkIcon boxSize={3} color="whiteAlpha.600" />
                </HStack>
              </Link>
            )}
            {previewUrl && (
              <Link
                href={previewUrl}
                isExternal
                _hover={{ textDecoration: 'none', opacity: 0.8 }}
              >
                <HStack
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="xl"
                  px={5}
                  py={3}
                  spacing={3}
                  _hover={{ bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.300' }}
                  transition="all 0.15s ease"
                >
                  <Icon type="Browser" size={20} />
                  <Text fontSize="small" fontWeight="semibold">
                    Live Preview
                  </Text>
                  <ExternalLinkIcon boxSize={3} color="whiteAlpha.600" />
                </HStack>
              </Link>
            )}
            {!repositoryUrl && !previewUrl && (
              <Text fontSize="small" opacity="secondary">
                No preview available for this external solution.
              </Text>
            )}
          </VStack>
        </Center>
      ) : (
        <>
          <CodeFrame url={codeFrameUrl} />

          {/* Real-time design score bar — only visible when design overlay is active */}
          {scoreConfig && isOverlayVisible && (
            <DevSandboxTooltip
              label={
                designScore === null
                  ? 'Analyzing design match...'
                  : `Design match: ${designScore}/100`
              }
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                height="4px"
                zIndex={8}
                bg="whiteAlpha.200"
                pointerEvents="auto"
                cursor="default"
              >
                <Box
                  height="100%"
                  width={designScore !== null ? `${designScore}%` : '0%'}
                  bg={
                    designScore === null
                      ? 'whiteAlpha.300'
                      : designScore >= 50
                      ? 'green.400'
                      : 'red.400'
                  }
                  transition="width 0.8s ease, background-color 0.3s ease"
                  opacity={isComparing ? 0.6 : 1}
                  sx={
                    isComparing
                      ? {
                          animation: 'scoreBarPulse 1.2s ease-in-out infinite',
                          '@keyframes scoreBarPulse': {
                            '0%, 100%': { opacity: 0.4 },
                            '50%': { opacity: 0.8 },
                          },
                        }
                      : undefined
                  }
                />
                {designScore !== null && !isComparing && (
                  <Box
                    position="absolute"
                    top="6px"
                    right="8px"
                    pointerEvents="none"
                  >
                    <Text fontSize="xMicro" color="whiteAlpha.600">
                      {designScore}/100
                    </Text>
                  </Box>
                )}
              </Box>
            </DevSandboxTooltip>
          )}

          {/* Design overlay */}
          {canShowOverlay && isOverlayVisible && activeScreenshotUrl && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              pointerEvents="none"
              zIndex={5}
              overflow="hidden"
            >
              <Image
                src={activeScreenshotUrl}
                opacity={overlayOpacity / 100}
                width="100%"
                height={hasMultipleScreenshots ? 'auto' : '100%'}
                objectFit={hasMultipleScreenshots ? undefined : 'fill'}
                fallback={<></>}
              />
            </Box>
          )}

        </>
      )}
    </Box>
  );
};

export default BrowserTab;
